import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { http, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, MessageCircleQuestion } from "lucide-react";
import { toast } from "sonner";

export default function Sources() {
  const qc = useQueryClient();
  const [newName, setNewName] = useState("");

  const { data: patients = [] } = useQuery({
    queryKey: ["patients"],
    queryFn: async () => (await http.get("/patients")).data,
  });
  const { data: sourceData } = useQuery({
    queryKey: ["sources"],
    queryFn: async () => (await http.get("/sources")).data,
  });

  const addSrc = useMutation({
    mutationFn: async (name) => http.post("/sources", { name }),
    onSuccess: () => { setNewName(""); qc.invalidateQueries({ queryKey: ["sources"] }); toast.success("Source added"); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const delSrc = useMutation({
    mutationFn: async (name) => http.delete(`/sources/${encodeURIComponent(name)}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["sources"] }); toast.success("Source removed"); },
  });

  const perf = useMemo(() => {
    const map = new Map();
    for (const p of patients) {
      const s = p.acquisition_source;
      if (!map.has(s)) map.set(s, { source: s, enquiries: 0, consultations: 0, converted: 0, revenue: 0 });
      const row = map.get(s);
      row.enquiries++;
      if (["Consultation Completed","Converted to Treatment"].includes(p.enquiry_status)) row.consultations++;
      if (p.enquiry_status === "Converted to Treatment") { row.converted++; row.revenue += Number(p.revenue) || 0; }
    }
    return Array.from(map.values()).map((r) => ({
      ...r,
      rate: r.enquiries ? Math.round((r.converted / r.enquiries) * 100) : 0,
    })).sort((a, b) => b.enquiries - a.enquiries);
  }, [patients]);

  const highlights = useMemo(() => {
    if (!perf.length) return null;
    const byVolume = perf[0];
    const byRate = [...perf].filter((r) => r.enquiries >= 3).sort((a, b) => b.rate - a.rate)[0];
    const byRevenue = [...perf].sort((a, b) => b.revenue - a.revenue)[0];
    return { byVolume, byRate, byRevenue };
  }, [perf]);

  const customList = sourceData?.custom || [];

  return (
    <div className="space-y-5" data-testid="sources-page">
      <div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight text-zinc-900">Sources</h1>
        <p className="text-sm text-zinc-500 mt-1">Which channels are actually working?</p>
      </div>

      <Card className="p-4 bg-emerald-50/60 border-emerald-200">
        <div className="flex gap-3 items-start">
          <MessageCircleQuestion className="h-5 w-5 text-emerald-700 mt-0.5 shrink-0" />
          <div className="text-sm text-emerald-900">
            <span className="font-medium">Remember to ask every new patient:</span> &quot;How did you hear about our clinic?&quot; Consistent source tracking is what makes this dashboard useful.
          </div>
        </div>
      </Card>

      {highlights && (
        <div className="grid sm:grid-cols-3 gap-3">
          <HighlightCard title="Highest number of patients" source={highlights.byVolume.source} sub={`${highlights.byVolume.enquiries} enquiries`} />
          {highlights.byRate && <HighlightCard title="Highest conversion rate" source={highlights.byRate.source} sub={`${highlights.byRate.rate}% conversion`} />}
          <HighlightCard title="Highest revenue generated" source={highlights.byRevenue.source} sub={`₹ ${highlights.byRevenue.revenue.toLocaleString()}`} />
        </div>
      )}

      <Card className="border-zinc-200 overflow-hidden">
        {perf.length === 0 ? (
          <div className="p-10 text-center text-zinc-500 text-sm">Add patients to see source performance.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-zinc-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Source</th>
                  <th className="text-right px-4 py-3">Enquiries</th>
                  <th className="text-right px-4 py-3">Consultations</th>
                  <th className="text-right px-4 py-3">Converted</th>
                  <th className="text-right px-4 py-3">Rate</th>
                  <th className="text-right px-4 py-3">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {perf.map((r) => (
                  <tr key={r.source} className="border-t border-zinc-100 hover:bg-zinc-50/50" data-testid={`source-perf-${r.source}`}>
                    <td className="px-4 py-3 font-medium text-zinc-900">{r.source}</td>
                    <td className="px-4 py-3 text-right text-zinc-700">{r.enquiries}</td>
                    <td className="px-4 py-3 text-right text-zinc-700">{r.consultations}</td>
                    <td className="px-4 py-3 text-right text-zinc-700">{r.converted}</td>
                    <td className="px-4 py-3 text-right">
                      <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 font-medium">{r.rate}%</Badge>
                    </td>
                    <td className="px-4 py-3 text-right text-zinc-700">₹ {r.revenue.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="p-5 border-zinc-200">
        <h3 className="font-heading text-base font-semibold text-zinc-900">Custom sources</h3>
        <p className="text-xs text-zinc-500 mt-0.5">Add extra channels specific to your clinic.</p>
        <div className="mt-4 flex gap-2">
          <Input placeholder="e.g. Local newspaper" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="custom-source-input" />
          <Button onClick={() => newName.trim() && addSrc.mutate(newName.trim())} className="bg-emerald-600 hover:bg-emerald-700 gap-1" data-testid="add-custom-source">
            <Plus className="h-4 w-4" /> Add
          </Button>
        </div>
        {customList.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {customList.map((c) => (
              <div key={c} className="inline-flex items-center gap-2 border border-zinc-200 rounded-full pl-3 pr-1 py-1 bg-white">
                <span className="text-sm text-zinc-700">{c}</span>
                <button onClick={() => delSrc.mutate(c)} className="h-6 w-6 rounded-full hover:bg-zinc-100 flex items-center justify-center" data-testid={`del-custom-${c}`}>
                  <Trash2 className="h-3.5 w-3.5 text-zinc-500" />
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function HighlightCard({ title, source, sub }) {
  return (
    <Card className="p-5 border-zinc-200">
      <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">{title}</div>
      <div className="mt-2 font-heading text-lg font-semibold text-zinc-900">{source}</div>
      <div className="text-sm text-emerald-700 mt-0.5">{sub}</div>
    </Card>
  );
}
